import React from 'react';
import { useTranslation } from 'react-i18next';
import { PublicReview } from '../../../model/client/client-review-model';

interface ReviewCardProps {
  review: PublicReview;
}

/**
 * Composant pour afficher une carte d'avis client
 */
const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const { t } = useTranslation();
  
  // Fonction pour afficher les étoiles
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
        <meta itemProp="worstRating" content="1" />
        <meta itemProp="bestRating" content="5" />
        <meta itemProp="ratingValue" content={rating.toString()} />
        {[...Array(5)].map((_, i) => (
          <svg 
            key={i} 
            className={`h-4 w-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };
  
  // Fonction pour formater la date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };
  
  return (
    <article 
      className="bg-white rounded-lg shadow-sm p-4 transition-all hover:shadow-md"
      itemScope 
      itemType="https://schema.org/Review"
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium" itemProp="author">
              {review.client?.firstName} {review.client?.lastName.charAt(0)}.
            </span>
            <span className="text-xs text-gray-500">
              {formatDate(review.createdAt)}
            </span>
          </div>
          <div className="mt-1">
            {renderStars(review.rating)}
          </div>
        </div>
        {review.service?.id && (
          <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
            {review.service.id}
          </div>
        )}
      </div>
      
      {review.comment && (
        <p className="text-sm text-gray-700 mt-2" itemProp="reviewBody">
          {review.comment}
        </p>
      )}
      
      {review.response && (
        <div className="mt-3 pl-3 border-l-2 border-primary/30">
          <p className="text-xs font-medium text-gray-700">
            {t('reviews.professionalReply')}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {review.response}
          </p>
        </div>
      )}
    </article>
  );
};

export default ReviewCard;
